import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const STATUS_COLOR = {
  pending:  'var(--yellow)',
  approved: 'var(--green)',
  rejected: 'var(--red)',
};
const STATUS_LABEL = {
  pending:  '⏳ Kutmoqda',
  approved: '✅ Tasdiqlandi',
  rejected: '❌ Rad etildi',
};

export default function Refunds() {
  const { t } = useLang();
  const { labId } = useLab();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState('all');
  const [form, setForm]       = useState({ phone: '', amount: '', reason: '' });
  const [userId, setUserId]   = useState(null);
  const [userName, setUserName] = useState('');
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]         = useState(null);

  const load = useCallback(() => {
    if (!labId) return;
    setLoading(true);
    const params = filter !== 'all' ? `?lab_id=${labId}&status=${filter}` : `?lab_id=${labId}`;
    api.get(`/refunds${params}`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [labId, filter]);

  useEffect(() => { load(); }, [load]);

  const searchUser = async () => {
    const phone = form.phone.trim().replace(/\s/g, '');
    if (!phone) return;
    setSearching(true);
    setUserId(null); setUserName('');
    try {
      const r = await api.get(`/owner/clients?search=${encodeURIComponent(phone)}`);
      const users = r.data?.users || [];
      const u = users[0];
      if (u?.id) { setUserId(u.id); setUserName(u.name || u.phone); }
      else setMsg({ type: 'error', text: 'Foydalanuvchi topilmadi' });
    } catch { setMsg({ type: 'error', text: 'Qidiruvda xato' }); }
    finally { setSearching(false); }
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    const amount = parseFloat(String(form.amount).replace(/\s|,/g, ''));
    if (!labId || !userId || !(amount > 0)) {
      setMsg({ type: 'error', text: 'Foydalanuvchi va miqdor to\'ldiring' });
      return;
    }
    setSubmitting(true); setMsg(null);
    try {
      const r = await api.post('/refunds', {
        lab_id: labId, user_id: userId, amount, reason: form.reason || null,
      });
      const d = r.data;
      const bonusTxt = parseFloat(d.refund?.bonus_amount || 0) > 0
        ? ` + ${Number(d.refund.bonus_amount).toLocaleString()} so'm bonus ayirmasi` : '';
      setMsg({
        type: 'ok',
        text: d.needs_approval
          ? `So'rov yaratildi (${Number(amount).toLocaleString()} so'm${bonusTxt}). Owner/menejer tasdig'i kutilmoqda.`
          : `Refund tasdiqlandi: ${Number(amount).toLocaleString()} so'm${bonusTxt}.`,
      });
      setForm({ phone: '', amount: '', reason: '' });
      setUserId(null); setUserName('');
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Xato' });
    } finally { setSubmitting(false); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/refunds/${id}/approve`);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Xato'); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rad etish sababi:');
    if (reason === null) return;
    try {
      await api.post(`/refunds/${id}/reject`, { rejected_reason: reason });
      load();
    } catch (e) { alert(e.response?.data?.error || 'Xato'); }
  };

  const pending = items.filter(i => i.status === 'pending');

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Vazvrat (Qaytarish)</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
          Balans qaytarish so'rovlari — depozit va bonus birga ayiriladi.
        </div>
      </div>

      {/* YANGI REFUND SHAKLI */}
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 20, marginBottom: 24,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>+ Yangi qaytarish</div>
        <form onSubmit={submitRefund}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>TELEFON</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="text" placeholder="+998 90 123 45 67"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  style={{ width: 180, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}
                />
                <button type="button" onClick={searchUser} disabled={searching} style={{
                  background: 'var(--card2)', border: '1px solid var(--border)',
                  color: 'var(--text2)', padding: '9px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                }}>
                  {searching ? '...' : '🔍'}
                </button>
              </div>
              {userName && (
                <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>✓ {userName}</div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>MIQDOR</label>
              <input
                type="text" inputMode="numeric" placeholder="50000"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value.replace(/[^\d]/g, '') }))}
                style={{ width: 140, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>SABAB</label>
              <input
                type="text" placeholder="Ixtiyoriy..."
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                style={{ width: 200, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}
              />
            </div>

            <button type="submit" disabled={submitting || !userId} style={{
              background: !userId ? 'var(--card2)' : 'linear-gradient(135deg, var(--red), #b91c1c)',
              border: 'none', color: !userId ? 'var(--text3)' : '#fff',
              padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: !userId ? 'not-allowed' : 'pointer',
            }}>
              {submitting ? 'Yuborilmoqda...' : '↩ Qaytarish'}
            </button>
          </div>
        </form>

        {msg && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13,
            background: msg.type === 'ok' ? 'rgba(0,255,136,0.08)' : 'rgba(255,59,92,0.08)',
            border: `1px solid ${msg.type === 'ok' ? 'var(--green)' : 'var(--red)'}`,
            color: msg.type === 'ok' ? 'var(--green)' : 'var(--red)',
          }}>
            {msg.text}
          </div>
        )}
      </div>

      {/* PENDING — tezkor panel */}
      {pending.length > 0 && (
        <div style={{
          background: 'rgba(255,196,0,0.06)', border: '1px solid rgba(255,196,0,0.3)',
          borderRadius: 14, padding: 16, marginBottom: 20,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--yellow)', marginBottom: 12 }}>
            ⏳ Tasdiq kutmoqda ({pending.length})
          </div>
          {pending.map(r => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {r.user_name || '—'} · {r.user_phone || ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {Number(r.amount).toLocaleString()} so'm
                  {parseFloat(r.bonus_amount || 0) > 0 && (
                    <span style={{ color: 'var(--red)', marginLeft: 6 }}>
                      + {Number(r.bonus_amount).toLocaleString()} bonus ayirmasi
                    </span>
                  )}
                </div>
                {r.reason && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Sabab: {r.reason}</div>}
              </div>
              <button onClick={() => approve(r.id)} style={{
                background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
                color: 'var(--green)', padding: '6px 14px', borderRadius: 7, fontSize: 12, cursor: 'pointer',
              }}>✅ Tasdiqlash</button>
              <button onClick={() => reject(r.id)} style={{
                background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)',
                color: 'var(--red)', padding: '6px 14px', borderRadius: 7, fontSize: 12, cursor: 'pointer',
              }}>❌ Rad</button>
            </div>
          ))}
        </div>
      )}

      {/* FILTER */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            background: filter === s ? 'rgba(0,212,255,0.12)' : 'var(--card2)',
            border: `1px solid ${filter === s ? 'var(--cyan)' : 'var(--border)'}`,
            color: filter === s ? 'var(--cyan)' : 'var(--text2)',
            padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
          }}>
            {s === 'all' ? 'Hammasi' : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {/* RO'YXAT */}
      {loading ? (
        <div style={{ color: 'var(--text3)', textAlign: 'center', padding: 40 }}>Yuklanmoqda...</div>
      ) : items.length === 0 ? (
        <div style={{ background: 'var(--card2)', borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
          Refundlar topilmadi
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(r => {
            const bonus = parseFloat(r.bonus_amount || 0);
            const total = parseFloat(r.amount) + bonus;
            return (
              <div key={r.id} style={{
                background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 12, padding: 14,
                display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
              }}>
                <div style={{ minWidth: 80 }}>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                    background: STATUS_COLOR[r.status] + '22',
                    color: STATUS_COLOR[r.status],
                  }}>
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {r.user_name || 'Noma\'lum'}{r.user_phone ? ` · ${r.user_phone}` : ''}
                  </div>
                  {r.reason && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.reason}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--red)' }}>
                    −{Number(r.amount).toLocaleString()} so'm
                  </div>
                  {bonus > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      + {Number(bonus).toLocaleString()} bonus → jami {Number(total).toLocaleString()} so'm ayiriladi
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', minWidth: 100, textAlign: 'right' }}>
                  {new Date(r.created_at).toLocaleString('uz-UZ', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                  <br />
                  {r.requested_by_role}
                  {r.approved_by_role && ` → ${r.approved_by_role}`}
                </div>
                {r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => approve(r.id)} style={{
                      background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
                      color: 'var(--green)', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                    }}>✅</button>
                    <button onClick={() => reject(r.id)} style={{
                      background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)',
                      color: 'var(--red)', padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                    }}>❌</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
